import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight, Download, MessageCircle } from "lucide-react";

import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { AdminCard, ListState } from "@/components/admin/primitives";
import { useDebounced } from "@/hooks/use-debounced";
import { formatDate } from "@/lib/dates";
import { countLabel, formatBRL } from "@/lib/format";
import { useCustomers, type CustomerRow } from "@/lib/queries";

/**
 * Lista de clientes.
 *
 * Nao existe cadastro de cliente: cada linha e a consolidacao dos pedidos de
 * um mesmo WhatsApp, agregada pela API. Nome e e-mail vem do pedido mais
 * recente do contato — o dado mais atual que existe.
 */
export const Route = createFileRoute("/admin/_painel/clientes/")({
  head: () => ({ meta: [{ title: "Clientes — Painel MonteBox" }] }),
  component: AdminClientes,
});

const COLUMNS = "min-w-[1040px] grid grid-cols-[300px_180px_100px_160px_180px_1fr] items-center gap-4";

/** Gera e baixa o CSV da lista que esta na tela. */
const exportCsv = (rows: CustomerRow[]) => {
  const header = ["Cliente", "WhatsApp", "E-mail", "Pedidos", "Último pedido", "Total"];
  const body = rows.map((row) => [
    row.name,
    row.whatsapp,
    row.email ?? "",
    String(row.orders),
    formatDate(row.lastOrderAt),
    row.total.toFixed(2).replace(".", ","),
  ]);

  // Aspas duplicadas: nome com virgula ou aspas nao pode quebrar a coluna.
  const csv = [header, ...body]
    .map((line) => line.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(";"))
    .join("\n");

  // BOM: sem ele o Excel em pt-BR abre o arquivo com os acentos errados.
  const bom = String.fromCharCode(0xfeff);
  const blob = new Blob([bom, csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `clientes-montebox-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

function AdminClientes() {
  const [query, setQuery] = useState("");
  const search = useDebounced(query, 300);
  const customers = useCustomers(search);

  const items = customers.data?.items ?? [];

  return (
    <>
      <AdminTopbar
        title="Clientes"
        search={{
          placeholder: "Buscar por nome, WhatsApp ou e-mail",
          value: query,
          onChange: setQuery,
        }}
        actions={
          <button
            type="button"
            onClick={() => exportCsv(items)}
            disabled={items.length === 0}
            className="flex items-center gap-1.5 rounded-full border border-mb-line bg-white px-4 py-2 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </button>
        }
      />

      <div className="flex flex-col gap-6 p-4 sm:p-8">
        <p className="text-sm text-mb-muted text-pretty">
          Cada cliente é a soma dos pedidos feitos com o mesmo WhatsApp. Não há cadastro separado —
          nome e e-mail vêm do pedido mais recente.
        </p>

        <AdminCard className="overflow-x-auto">
          <div className={`${COLUMNS} border-b border-mb-line bg-mb-surface px-6 py-3`}>
            {["Cliente", "WhatsApp", "Pedidos", "Último pedido", "Total"].map((head) => (
              <span
                key={head}
                className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted"
              >
                {head}
              </span>
            ))}
            <span className="text-right text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted">
              Ações
            </span>
          </div>

          <ListState
            loading={customers.isLoading}
            error={customers.error}
            empty={items.length === 0}
            emptyLabel="Nenhum cliente encontrado com essa busca."
          />

          {items.map((customer) => (
            <div
              key={customer.key}
              className={`${COLUMNS} border-b border-mb-line px-6 py-3.5 last:border-b-0`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-mb-surface font-display text-[13px] font-bold text-mb-navy">
                  {initials(customer.name)}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-mb-ink">{customer.name}</p>
                  <p className="truncate text-[11px] leading-4 text-mb-muted">
                    {customer.email ?? "Sem e-mail cadastrado"}
                  </p>
                </div>
              </div>

              <span className="text-sm tabular-nums text-mb-muted">{customer.whatsapp}</span>
              <span className="text-sm tabular-nums text-mb-muted">{customer.orders}</span>
              <span className="text-sm tabular-nums text-mb-muted">
                {formatDate(customer.lastOrderAt)}
              </span>
              <span className="text-sm font-bold tabular-nums text-mb-navy">
                {formatBRL(customer.total)}
              </span>

              <div className="flex items-center justify-self-end gap-1.5">
                <a
                  href={`https://wa.me/55${customer.key}`}
                  target="_blank"
                  rel="noreferrer"
                  title={`Chamar ${customer.name} no WhatsApp`}
                  aria-label={`Chamar ${customer.name} no WhatsApp`}
                  className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                >
                  <MessageCircle className="h-[15px] w-[15px]" />
                </a>
                <Link
                  to="/admin/clientes/$clienteId"
                  params={{ clienteId: customer.key }}
                  className="flex items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-[7px] text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                >
                  Ver pedidos
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </AdminCard>

        <p className="text-xs text-mb-muted">
          Mostrando {items.length} de{" "}
          {countLabel(customers.data?.total ?? 0, "cliente", "clientes")}
        </p>
      </div>
    </>
  );
}

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
